import type { TalkBrief } from './brief'

/**
 * The proxy's own cap, in the client's copy. A brief over it comes back 413
 * and every reply for the rest of the conversation is the offline fallback,
 * with nothing on screen to say why - which is the worst failure this system
 * has, because it looks like a technician who has gone vague.
 *
 * Exported so `npm run talk` can assert it against the proxy's own
 * `TALK_MAX_BRIEF`: two copies of a number across a seam the client cannot
 * import over, the same way the unprompted marker is kept in step. THE RULE
 * IS `MAX_BRIEF <= TALK_MAX_BRIEF` AS DEPLOYED, not as written in the repo:
 * a hosting deploy that raises this ahead of the functions deploy sends
 * briefs the live proxy refuses. Functions first, then hosting. See TALK.md
 * ("The brief that fits").
 *
 * 16 KB SINCE 2026-09-18. It was 8192, set when a full brief ran to 4 KB.
 * Measured honestly - an open work order, a report on the screen, a scene
 * up, a building fault, the real experiment menu and every history list at
 * its cap - Sarah's Spanish brief is 9.7 KB of FACTS and about 13 KB with
 * her memory, so under the old cap she forgot five of six conversations in
 * ordinary play. The server's number is an abuse guard, not a provider
 * limit (functions/talkPrompt.js), so it moved: 13 KB plus a quarter.
 */
export const MAX_BRIEF = 16384
/**
 * The cap every proxy deployed before 2026-09-18 enforces. `askTechnician`
 * retries at this size when a proxy answers 413, so a new client in front
 * of an old proxy degrades to the old behaviour instead of going offline.
 */
export const LEGACY_MAX_BRIEF = 8192

/** `reported.findings` when the brief is under pressure: the opening of it. */
const FINDINGS_UNDER_PRESSURE = 300
/** The least a persona is cut to by the last-resort guard. */
const PERSONA_FLOOR = 120

const size = (b: TalkBrief): number => JSON.stringify(b).length

/**
 * Shorten the brief until it fits: DESCRIPTIONS first, MEMORY last.
 *
 * Everything in the brief is capped at its source, and at today's cap a
 * real brief at every one of those caps at once fits untouched (`npm run
 * talk` measures it). This is the guard for the day that stops being true -
 * the directory grows every time the floor does, the caps are in five
 * files, and going over is silent.
 *
 * THE ORDER, each step taken only if the one before was not enough:
 *
 *   1. scene `about` lines          - the title beside each says it again
 *   2. experiment `summary` lines   - the example beside each says it again
 *   3. the hall-layout paragraph    - the directory still places every tool
 *   4. the `watch` line of the scene that is up - its id and title stay,
 *      so she still knows WHAT is on the screen between them
 *   5. colleagues' roles on the roster - names and places stay, which is
 *      what a hand-off needs ("Emily, at CMP-11 in bay 08")
 *   6. the report's findings, cut to their opening sentences
 *   7. THEN memory, the oldest remembered thing first, evenly across the
 *      four history lists
 *   8. and if all of that was not enough - which is a bug in whatever grew
 *      the brief - the report, the scene-up note, the persona and at the very
 *      last the directory's tail are cut until it fits, with a console warning. An oversize brief is
 *      NEVER sent: the proxy would refuse it and the technician would go
 *      vague for the rest of the conversation with nothing to say why.
 *
 * What never goes: every scene id and title, every experiment id and
 * example (the whitelists `takeDemo` and `takeRun` resolve against), the
 * tool directory, every colleague's name and place, the screen, and the
 * work order. A brief that lied about those to save bytes would be worse
 * than a long one.
 *
 * Only the copy sent to the proxy is fitted. The offline technician answers
 * from the brief as built (talkClient.ts `localAnswer`).
 */
export function fitBrief(brief: TalkBrief, cap: number = MAX_BRIEF): TalkBrief {
  if (size(brief) <= cap) return brief
  const b: TalkBrief = { ...brief, floor: { ...brief.floor }, taught: brief.taught ? { ...brief.taught } : null }
  // Optional-chained throughout because a brief arriving here from anywhere
  // but `buildBrief` (a harness, a future caller) need not carry every block.
  if (b.demos?.some((d) => d.about)) {
    b.demos = b.demos.map((d) => ({ ...d, about: '' }))
    if (size(b) <= cap) return b
  }
  if (b.experiments?.menu?.some((m) => m.summary)) {
    b.experiments = { ...b.experiments, menu: b.experiments.menu.map((m) => ({ ...m, summary: '' })) }
    if (size(b) <= cap) return b
  }
  if (b.floor.layout) {
    b.floor = { ...b.floor, layout: '' }
    if (size(b) <= cap) return b
  }
  if (b.onScreen?.watch) {
    b.onScreen = { ...b.onScreen, watch: '' }
    if (size(b) <= cap) return b
  }
  if (b.floor.roster?.some((r) => r.role)) {
    b.floor = { ...b.floor, roster: b.floor.roster.map((r) => ({ ...r, role: '' })) }
    if (size(b) <= cap) return b
  }
  if (b.reported && b.reported.findings.length > FINDINGS_UNDER_PRESSURE) {
    b.reported = { ...b.reported, findings: b.reported.findings.slice(0, FINDINGS_UNDER_PRESSURE).trimEnd() + '…' }
    if (size(b) <= cap) return b
  }
  // Memory. Longest list first, so a brief over the cap loses depth evenly
  // rather than losing one list entirely.
  const lists = (): Array<{ n: number; drop: () => void }> => [
    { n: b.floor.round?.length ?? 0, drop: () => (b.floor.round = b.floor.round!.slice(1)) },
    { n: b.taught?.covered.length ?? 0, drop: () => (b.taught = { ...b.taught!, covered: b.taught!.covered.slice(1) }) },
    { n: b.floor.worked?.length ?? 0, drop: () => (b.floor.worked = b.floor.worked!.slice(1)) },
    { n: b.work?.length ?? 0, drop: () => (b.work = b.work!.slice(1)) },
  ]
  for (;;) {
    const longest = lists().sort((x, y) => y.n - x.n)[0]
    if (!longest || longest.n === 0) break
    longest.drop()
    if (size(b) <= cap) return b
  }
  // THE LAST RESORT. Nothing left to forget and still over: whatever grew
  // this brief is a bug, and it is said out loud here rather than left to
  // surface as a 413 nobody sees.
  console.warn(`fitBrief: ${size(b)} B after shedding every description and memory, cap ${cap} B - cutting the report, the scene note and the persona`)
  if (b.reported) {
    b.reported = null
    if (size(b) <= cap) return b
  }
  if (b.onScreen) {
    b.onScreen = null
    if (size(b) <= cap) return b
  }
  const persona = b.person?.persona ?? ''
  if (persona.length > PERSONA_FLOOR) {
    const keep = Math.max(PERSONA_FLOOR, persona.length - (size(b) - cap) - 1)
    b.person = { ...b.person, persona: persona.slice(0, keep) }
    if (size(b) <= cap) return b
  }
  // Past even that the brief was never a real one (a directory of hundreds
  // of tools, say). The directory's process names go, then its tail - tags
  // and bays of what is left stay true - because the one thing this function
  // promises is that what it returns FITS.
  if (b.floor.directory?.length) {
    b.floor = { ...b.floor, directory: b.floor.directory.map((d) => ({ ...d, process: '' })) }
    while (size(b) > cap && b.floor.directory.length > 0) {
      b.floor = { ...b.floor, directory: b.floor.directory.slice(0, Math.floor(b.floor.directory.length * 0.8)) }
    }
  }
  if (size(b) > cap) console.warn(`fitBrief: still ${size(b)} B against ${cap} B with nothing left to cut`)
  return b
}
